import { useCallback, useRef, useState } from 'react';
import { ConnectionState, Participant, Track } from 'livekit-client';
import {
  useConnectionState, useIsMuted, useIsEncrypted, isTrackReference, ParticipantTile, VideoTrack,
  ParticipantPlaceholder, ParticipantName, ConnectionQualityIndicator,
  LockLockedIcon, ScreenShareIcon,
  type TrackReferenceOrPlaceholder,
} from '@livekit/components-react';
import { Loader2, Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';
import { useLiveKit } from '../contexts';

/** One controller per stage: camera and screen-share tiles share the same in-flight lock. */
export function useParticipantMicrophoneControl() {
  const { room, meeting, isHostOrCoHost, muteParticipant, unmuteParticipant } = useLiveKit();
  const connectionState = useConnectionState(room);
  const isConnected = connectionState === ConnectionState.Connected;
  const inFlight = useRef(new Set<string>());
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const canControl = useCallback((participant: Participant) => {
    if (!room || !meeting || !isConnected || !isHostOrCoHost) return false;
    const target = meeting.participants.find(p => p.identity === participant.identity || p.userId === participant.identity);
    const local = participant === room.localParticipant;
    // Match ControlPanel's protection of a remote host; a host can control their own microphone.
    return (local || (room.remoteParticipants.has(participant.identity) && target?.role !== 'host')) &&
      participant.permissions?.canPublish !== false && target?.canPublish !== false;
  }, [room, meeting, isConnected, isHostOrCoHost]);
  const toggle = useCallback(async (participant: Participant) => {
    const identity = participant.identity;
    if (!canControl(participant) || inFlight.current.has(identity)) return;
    inFlight.current.add(identity);
    setPending(new Set(inFlight.current));
    try {
      const enabled = participant.isMicrophoneEnabled;
      if (participant === room?.localParticipant) {
        await room.localParticipant.setMicrophoneEnabled(!enabled);
      } else if (enabled) {
        await muteParticipant(identity);
      } else {
        await unmuteParticipant(identity);
      }
      // The icon follows LiveKit's actual publication state, never an optimistic API result.
    } catch {
      toast.error('Không điều khiển được mic. Vui lòng thử lại.');
    } finally {
      inFlight.current.delete(identity);
      setPending(new Set(inFlight.current));
    }
  }, [canControl, room, muteParticipant, unmuteParticipant]);
  return { canControl, toggle, pending, isHostOrCoHost };
}

type Controls = ReturnType<typeof useParticipantMicrophoneControl>;
export function ParticipantMicrophoneControl({ participant, controls }: { participant: Participant; controls: Controls }) {
  // Subscribe even when there is no camera or the tile shows screen sharing.
  const muted = useIsMuted({ participant, source: Track.Source.Microphone });
  const enabled = participant.isMicrophoneEnabled && !muted;
  const busy = controls.pending.has(participant.identity);
  const allowed = controls.canControl(participant);
  const name = participant.name || participant.identity;
  const action = `${enabled ? 'Tắt' : 'Bật'} mic của ${name}`;
  return <button type="button"
    aria-label={action} aria-pressed={enabled} aria-busy={busy}
    title={busy ? 'Đang gửi lệnh điều khiển mic…' : allowed ? action : `Mic của ${name}: ${enabled ? 'đang bật' : 'đang tắt'}`}
    disabled={!allowed || busy}
    className="pointer-events-auto flex h-5 w-5 shrink-0 items-center justify-center rounded bg-transparent text-white hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-default disabled:opacity-70"
    onPointerDown={event => event.stopPropagation()}
    onDoubleClick={event => event.stopPropagation()}
    onClick={event => { event.stopPropagation(); void controls.toggle(participant); }}>
    {busy ? <Loader2 className="size-4 animate-spin"/> : enabled ? <Mic className="size-4 text-green-400"/> : <MicOff className="size-4 text-gray-300"/>}
  </button>;
}

/** Replace the SDK tile's contents, so the original metadata is never rendered twice. */
export function ControlledParticipantTile({ trackRef, controls }: {
  trackRef: TrackReferenceOrPlaceholder;
  controls: Controls;
}) {
  const encrypted = useIsEncrypted(trackRef.participant);
  return <ParticipantTile trackRef={trackRef} className="h-full w-full">
    {isTrackReference(trackRef) && <VideoTrack trackRef={trackRef}/>}
    <div className="lk-participant-placeholder"><ParticipantPlaceholder/></div>
    <div className="lk-participant-metadata">
      <div className="lk-participant-metadata-item">
        {encrypted && <LockLockedIcon style={{ marginRight: '.25rem' }}/>}
        <ParticipantMicrophoneControl participant={trackRef.participant} controls={controls}/>
        {trackRef.source === Track.Source.ScreenShare && <ScreenShareIcon style={{ marginRight: '.25rem' }}/>}
        <ParticipantName/>
      </div>
      <ConnectionQualityIndicator className="lk-participant-metadata-item"/>
    </div>
  </ParticipantTile>;
}
